import { t as tr } from './i18n';
/**
 * Etapa unei cereri în lista „Cereri de ofertă”, cu aceleași reguli ca „Azi”
 * (`todayView`): de decis când au răspuns toți sau când termenul a trecut și
 * ai măcar două oferte; întârziată când termenul a trecut fără ele.
 */

export type RfqStage =
  | 'draft'
  | 'waiting'
  | 'late'
  | 'decide'
  | 'toOrder'
  | 'approval'
  | 'ordered'
  | 'closed'
  | 'canceled';

/** Ce trebuie știut despre o cerere ca să-i găsești etapa. */
export interface RfqStageFacts {
  deadline: string;
  closedAt: string | Date | null;
  canceledAt: string | Date | null;
  chosen: boolean;
  /** comanda din cerere: emisă, la aprobare sau niciuna în vigoare */
  order: 'issued' | 'pending' | null;
  sends: { sent: boolean; hasOffer: boolean }[];
}

export function rfqStage(r: RfqStageFacts, today: string): RfqStage {
  if (r.canceledAt) return 'canceled';
  if (r.order === 'issued') return 'ordered';
  if (r.order === 'pending') return 'approval';
  if (r.chosen) return 'toOrder';
  if (r.closedAt) return 'closed';
  const offers = r.sends.filter((s) => s.hasOffer).length;
  if (!r.sends.some((s) => s.sent || s.hasOffer)) return 'draft';
  const waiting = r.sends.length - offers;
  const overdue = r.deadline < today;
  if ((waiting === 0 && offers > 0) || (overdue && offers > 1)) return 'decide';
  return overdue ? 'late' : 'waiting';
}

export const RFQ_STAGE: Record<RfqStage, { label: string; pill: string }> = {
  draft: {
    get label() {
      return tr('neplecată');
    },
    pill: 'off',
  },
  waiting: {
    get label() {
      return tr('așteaptă oferte');
    },
    pill: 'todo',
  },
  late: {
    get label() {
      return tr('termen depășit');
    },
    pill: 'late',
  },
  decide: {
    get label() {
      return tr('de decis');
    },
    pill: 'act',
  },
  toOrder: {
    get label() {
      return tr('de comandat');
    },
    pill: 'act',
  },
  approval: {
    get label() {
      return tr('la aprobare');
    },
    pill: 'wait',
  },
  ordered: {
    get label() {
      return tr('comandată');
    },
    pill: 'got',
  },
  closed: {
    get label() {
      return tr('închisă');
    },
    pill: 'off',
  },
  canceled: {
    get label() {
      return tr('anulată');
    },
    pill: 'off',
  },
};

/** Contoarele de sus. „De lucru” = tot ce mai cere ceva de la tine. */
export const RFQ_GROUPS = [
  {
    id: 'active',
    get label() {
      return tr('De lucru');
    },
    stages: ['draft', 'waiting', 'late', 'decide', 'toOrder', 'approval'],
  },
  {
    id: 'draft',
    get label() {
      return tr('Neplecate');
    },
    stages: ['draft'],
  },
  {
    id: 'waiting',
    get label() {
      return tr('Așteaptă oferte');
    },
    stages: ['waiting'],
  },
  {
    id: 'late',
    get label() {
      return tr('Termen depășit');
    },
    stages: ['late'],
  },
  {
    id: 'decide',
    get label() {
      return tr('De decis');
    },
    stages: ['decide'],
  },
  {
    id: 'toOrder',
    get label() {
      return tr('De comandat');
    },
    stages: ['toOrder'],
  },
  {
    id: 'approval',
    get label() {
      return tr('La aprobare');
    },
    stages: ['approval'],
  },
  {
    id: 'ordered',
    get label() {
      return tr('Comandate');
    },
    stages: ['ordered'],
  },
  {
    id: 'closed',
    get label() {
      return tr('Închise');
    },
    stages: ['closed'],
  },
  {
    id: 'canceled',
    get label() {
      return tr('Anulate');
    },
    stages: ['canceled'],
  },
] as const satisfies readonly { id: string; label: string; stages: readonly RfqStage[] }[];

export type RfqGroup = (typeof RFQ_GROUPS)[number]['id'];

export const isRfqGroup = (v: string): v is RfqGroup => RFQ_GROUPS.some((g) => g.id === v);

export function stageIn(stage: RfqStage, group: RfqGroup): boolean {
  return (RFQ_GROUPS.find((g) => g.id === group)!.stages as readonly RfqStage[]).includes(stage);
}
